import { type ConnectedPosition, Overlay, OverlayPositionBuilder, type OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  type OnDestroy,
  type OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  type TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { filter, type Subscription } from 'rxjs';
import {
  POPOVER_OVERLAY_DATA,
  PopoverContainerComponent,
} from '../../components/popover-container/popover-container.component';
import { dropDownAlignment } from '../../components/dropdown-list/dropdown-list.component';
import { POPOVER_POSITIONS_MAP } from './popover-positions-map';

export type PopoverTrigger = 'click' | 'hover' | null;

@Directive({
  selector: '[workUiPopover]',
  standalone: true,
  exportAs: 'workUiPopover',
})
export class PopoverDirective implements OnInit, OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly platformId = inject(PLATFORM_ID);

  public readonly workUiPopover = input.required<TemplateRef<void>>();
  public readonly workUiPopoverTrigger = input<PopoverTrigger>('click');
  public readonly workUiPopoverPosition = input<dropDownAlignment>('bottom');
  public readonly workUiPopoverHoverHideDelay = input<number>(100);
  public readonly workUiPopoverWidth = input<'auto' | 'fitParent'>('auto');
  public readonly workUiPopoverMinWidth = input<number>(200);
  public readonly workUiPopoverMaxHeight = input<number | string | undefined>(undefined);
  public readonly workUiPopoverOrigin = input<ElementRef>();
  public readonly workUiPopoverVisible = model<boolean>(false);
  public readonly workUiPopoverClosedWithOutsidePointerEvent = output();

  private overlayRef?: OverlayRef;
  private overlayRefSubscription?: Subscription;
  private listeners: (() => void)[] = [];
  private overlayHoverListeners: (() => void)[] = [];
  private hideTimeout?: ReturnType<typeof setTimeout>;

  private popoverVisibleEffect = effect(() => {
    if (this.workUiPopoverVisible()) {
      this.show();
    } else {
      this.hide();
    }
  });

  private popoverTriggerEffect = effect(() => {
    if (this.listeners.length) {
      this.unlistenAll();
    }
    this.setupTriggers();
    this.overlayRefSubscription?.unsubscribe();
    this.overlayRefSubscription = undefined;
    if (this.workUiPopoverTrigger() !== 'hover') {
      this.subscribeToOverlayRef();
    }
  });

  get nativeElement() {
    return this.workUiPopoverOrigin()?.nativeElement ?? this.elementRef.nativeElement;
  }

  ngOnInit() {
    this.createOverlay();
  }

  ngOnDestroy() {
    this.unlistenAll();
    this.overlayRefSubscription?.unsubscribe();
    this.overlayRef?.dispose();
    this.clearHideTimeout();
  }

  show() {
    if (!this.overlayRef) {
      this.createOverlay();
    }

    this.adjustPopoverWidth();

    const injector = Injector.create({
      providers: [
        {
          provide: POPOVER_OVERLAY_DATA,
          useValue: this.workUiPopover(),
        },
      ],
    });
    const componentPortal = new ComponentPortal(PopoverContainerComponent, this.viewContainerRef, injector);
    this.overlayRef?.attach(componentPortal);

    if (this.workUiPopoverTrigger() === 'hover') {
      this.addOverlayHoverListeners();
    }
  }

  hide() {
    this.overlayRef?.detach();
    if (this.workUiPopoverTrigger() === 'hover') {
      this.removeOverlayHoverListeners();
    }
  }

  toggle() {
    this.workUiPopoverVisible.set(!this.workUiPopoverVisible());
  }

  private createOverlay() {
    if (isPlatformBrowser(this.platformId)) {
      const positionStrategy = this.overlayPositionBuilder
        .flexibleConnectedTo(this.nativeElement)
        .withPositions(this.getPositions())
        .withPush(false)
        .withViewportMargin(8);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: false,
        minWidth: this.workUiPopoverMinWidth(),
        maxHeight: this.workUiPopoverMaxHeight(),
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
      });
    }
  }

  private adjustPopoverWidth() {
    if (this.workUiPopoverWidth() === 'fitParent') {
      const parentElement = this.nativeElement.parentElement;
      const minWidth = parentElement ? parentElement.getBoundingClientRect().width : 300;
      this.overlayRef?.updateSize({ minWidth });
    }
  }

  private subscribeToOverlayRef(): void {
    if (isPlatformBrowser(this.platformId) && this.overlayRef) {
      this.overlayRefSubscription = this.overlayRef
        .outsidePointerEvents()
        .pipe(filter((event) => !this.nativeElement.contains(event.target)))
        .subscribe(() => {
          this.workUiPopoverVisible.set(false);
          this.workUiPopoverClosedWithOutsidePointerEvent.emit();
        });
    }
  }

  private setupTriggers() {
    const trigger = this.workUiPopoverTrigger();
    if (!trigger) return;

    if (trigger === 'click') {
      this.addClickListener();
    } else if (trigger === 'hover') {
      this.addHoverListeners();
    }
  }

  private addClickListener() {
    this.listeners.push(
      this.renderer.listen(this.nativeElement, 'click', (event: Event) => {
        event.stopPropagation();
        this.toggle();
      }),
    );
  }

  private addHoverListeners() {
    this.listeners.push(
      this.renderer.listen(this.nativeElement, 'mouseenter', () => {
        this.clearHideTimeout();
        this.workUiPopoverVisible.set(true);
      }),
      this.renderer.listen(this.nativeElement, 'mouseleave', () => {
        this.scheduleHide();
      }),
    );
  }

  private unlistenAll() {
    for (const listener of this.listeners) {
      listener();
    }
    this.listeners = [];
  }

  private addOverlayHoverListeners() {
    if (this.overlayRef?.overlayElement) {
      this.overlayHoverListeners.push(
        this.renderer.listen(this.overlayRef.overlayElement, 'mouseenter', () => {
          this.clearHideTimeout();
        }),
      );

      this.overlayHoverListeners.push(
        this.renderer.listen(this.overlayRef.overlayElement, 'mouseleave', () => {
          this.scheduleHide();
        }),
      );
    }
  }

  private removeOverlayHoverListeners() {
    for (const listener of this.overlayHoverListeners) {
      listener();
    }
    this.overlayHoverListeners = [];
  }

  private scheduleHide() {
    this.hideTimeout = setTimeout(() => {
      this.workUiPopoverVisible.set(false);
    }, this.workUiPopoverHoverHideDelay());
  }

  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }
  }

  private getPositions(): ConnectedPosition[] {
    const placement = this.workUiPopoverPosition();
    return POPOVER_POSITIONS_MAP[placement] || POPOVER_POSITIONS_MAP['bottom'];
  }
}
